<template>
    <div class="corrections-list" :class="{ 'corrections-list--reviewer': isProofreadingReviewer }">
        <div class="corrections-list__container">
            <div class="corrections-list__topbar corrections-list__topbar--navigation">
                <button
                    type="button"
                    class="button button--gray"
                    style="max-width: 300px;"
                    @click="back"
                >
                    ‹ {{ $t('export.repair.backToBookEditing') }}
                </button>
                <RadioGroup
                    v-model="filter"
                    name="filter"
                    class="corrections-list__radio"
                    :options="states"
                    label="label"
                    allow-clear
                    compact
                />
            </div>
            <div
                v-if="showGetReport"
                class="corrections-list__topbar corrections-list__topbar--report"
            >
                <CorrectionsGetReport />
            </div>
            <div class="corrections-list__inner">
                <div :class="{ 'text__container--rtl': isRtl }">
                    <template v-if="corrections.length">
                        <CorrectionsListItem
                            v-for="correction in corrections"
                            :key="correction.id"
                            :class="{
                                'correction--active': active && active.id === correction.id
                            }"
                            :correction="correction"
                            :is-active="selected && selected.correction === correction.id"
                            :reviewer-card="isProofreadingReviewer"
                            @selected="select"
                            @view-in-book="viewInBook"
                            @openThread="openThread"
                            @deleted="deleted"
                            @update="onUpdate"
                        />
                    </template>
                    <!-- Checked before the generic guest branch: the embedded reviewer is a
                         guest too, and must always get the redesigned empty state. A
                         server-configured text (information.text) replaces the default hint. -->
                    <div
                        v-else-if="isProofreadingReviewer"
                        class="corrections-list__empty corrections-list__empty--reviewer"
                    >
                        <span class="corrections-list__empty-icon">
                            <IconMessageSquareDashed :size="24" />
                        </span>
                        <span class="corrections-list__empty-title">
                            {{ $t('corrections.list.reviewerEmptyTitle') }}
                        </span>
                        <span
                            v-if="information.text"
                            class="corrections-list__empty-hint"
                            v-html="information.text"
                        />
                        <span v-else class="corrections-list__empty-hint">
                            {{ $t('corrections.list.reviewerEmptyHint') }}
                        </span>
                    </div>
                    <div
                        v-else-if="isGuestAccess && information.text"
                        class="corrections-list__empty"
                        v-html="information.text"
                    ></div>
                    <div v-else class="corrections-list__empty">No corrections</div>
                    <div v-if="hasNext" class="text__pagination">
                        <button
                            type="button"
                            class="button text__button text-pagination__next"
                            @click="loadNext"
                        >
                            {{ $t('misc.loadmore') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CorrectionsListItem from '../CorrectionsListItem/CorrectionsListItem';
import CorrectionsGetReport from '../CorrectionsGetReport/CorrectionsGetReport';
import { RadioGroup } from '@speechki-frontend/shared/ui/forms';
import { IconMessageSquareDashed } from '@speechki-frontend/shared/ui/icons';

import { mapState, mapGetters } from 'vuex';
import { isRtlLanguage } from '@speechki-frontend/core/editor/utils';

export default {
    name: 'CorrectionsList',
    components: {
        CorrectionsListItem,
        RadioGroup,
        CorrectionsGetReport,
        IconMessageSquareDashed
    },
    inject: {
        isEmbed: {
            default: false
        }
    },
    props: {
        corrections: {
            type: Array,
            default: () => []
        },
        states: {
            type: Array,
            required: true
        },
        hasNext: {
            type: Boolean,
            default: false
        },
        selected: {
            type: Object,
            default: () => {}
        },
        active: {
            type: Object,
            default: null
        },
        defaultFilter: {
            type: String,
            default: ''
        },
        information: {
            type: Object,
            default: () => ({ text: '' })
        }
    },
    data() {
        return {
            filter: this.defaultFilter
        };
    },
    computed: {
        ...mapState({
            book: state => state.book.book
        }),
        ...mapGetters(['isGuestAccess', 'isReviewer']),
        ...mapGetters('guestAccess', ['guestAccessActionPermission']),
        showGetReport() {
            if (this.isGuestAccess) {
                return this.guestAccessActionPermission('book', 'corrections_report');
            }

            return true;
        },
        isRtl() {
            return isRtlLanguage(this.book.book_language);
        },
        isProofreadingReviewer() {
            return this.isEmbed && this.isReviewer;
        }
    },
    watch: {
        filter() {
            this.$emit('filter', this.filter);
        },
        corrections() {
            if (this.filter === 'fixed' && !this.corrections.length && !this.isProofreadingReviewer) {
                this.filter = 'in_progress';
            }
        }
    },
    methods: {
        back() {
            this.$emit('close');
        },
        select(event) {
            this.$emit('selected', event);
        },
        viewInBook(target) {
            this.$emit('view-in-book', target);
        },
        deleted(correction) {
            this.$emit('deleted', correction);
        },
        loadNext() {
            this.$emit('loadNext');
        },
        openThread(correction) {
            this.$emit('openThread', correction);
        },
        onUpdate(event) {
            this.$emit('update', event);
        }
    }
};
</script>

<style lang="scss" src="./CorrectionsList.scss"></style>
